import { Alert, Box, FormControl, Grid, IconButton, TextField, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import SectionFormBox from '../../../../components/SectionFormBox/SectionFormBox';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useEstablishmentSave from '../../../Empresas/EstablishmentSave/hooks/useEstablishmentSave';
import Input from '../../../../components/Input/Input';
import { useGetTiposResolucionQuery } from '../../../../services/avisoObraApi';
import { TipoResolucionItem } from '../../consts/avisoObraTypes';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { validateField, validateStep1 } from '../validations/validateStep1';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
interface Step1Props {
    t: TFunction;
    formData: any;
    handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    errors?: Record<string, string>;
    setErrors?: (errors: Record<string, string>) => void;
    isView?: boolean;
    idAvisoObra?: number | null;
}

const Step1: React.FC<Step1Props> = ({
    t,
    formData,
    handleChange,
    errors,
    setErrors,
    isView,
    idAvisoObra
}) => {
    const {
        provincesList,
        citiesList,
        selectedProvince,
        selectedCity,
        selectedPostalCode,
        loadingProvinces,
        loadingCities,
        handleProvinceSelection,
        handleCitySelection
    } = useEstablishmentSave();
    useEffect(() => {
        if (formData.idProvincia && provincesList?.data?.provincias?.length) {
            const provincia = provincesList.data.provincias.find(
                (p: any) => p.id === formData.idProvincia
            );
            if (provincia) {
                handleProvinceSelection(provincia);
            }
        }
    }, [formData.idProvincia, provincesList?.data?.provincias]);

    useEffect(() => {
        if (formData.idLocalidad && selectedProvince && citiesList?.data?.localidades?.length) {
            const localidad = citiesList.data.localidades.find(
                (l: any) => l.id === formData.idLocalidad
            );
            if (localidad) {
                handleCitySelection(localidad);
            }
        }
    }, [formData.idLocalidad, selectedProvince, citiesList?.data?.localidades]);

    useEffect(() => {
        if (selectedPostalCode) {
            handleChange({
                target: {
                    name: 'codigoPostal',
                    value: selectedPostalCode
                }
            } as any);
        }
    }, [selectedPostalCode]);

    const { data: resolutionTypeOptionsRaw = [], isLoading: loadingResolutionTypes } =
        useGetTiposResolucionQuery();

    const resolutionTypeOptions = resolutionTypeOptionsRaw.filter(
        (res: TipoResolucionItem) => res.id !== 1
    );

    const selectedResolutionType =
        resolutionTypeOptions.find((o) => o.id === formData?.idTipoResolucion) ?? null;

    const requiereLatLong =
        formData.numeroKm !== undefined &&
        formData.numeroKm !== '' &&
        Number(formData.numeroKm) === 0;

    const hasLatLng =
        formData.latitud !== undefined &&
        formData.latitud !== '' &&
        formData.longitud !== undefined &&
        formData.longitud !== '';
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const maxSize = 10 * 1024 * 1024;
        const isPdf = file.type === 'application/pdf';
        const resolucionLabel = formData.tipoResolucionName;

        const requiereArchivo = resolucionLabel !== 'No Corresponde';

        if (requiereArchivo) {
            if (!isPdf) {
                enqueueSnackbar('El archivo debe estar en formato PDF.', { variant: 'error' });
                return;
            }

            if (file.size > maxSize) {
                enqueueSnackbar('El archivo no puede superar los 10MB.', { variant: 'error' });
                return;
            }
        }

        handleChange({ target: { name: 'pdfPrograma', value: file } } as any);
        handleChange({ target: { name: 'nombreArchivo', value: file.name } } as any);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!setErrors) return;

        const { name, value } = e.target;

        const updatedFormData = { ...formData, [name]: value };

        const fieldErrors = validateStep1(updatedFormData);

        const newErrors = { ...(errors || {}) };

        if (fieldErrors[name]) {
            newErrors[name] = fieldErrors[name];
        } else {
            delete newErrors[name];
        }

        setErrors(newErrors);
    };

    const getErrorKey = (name: string): string => {
        switch (name) {
            case 'fechaInicio':
                return 'fechaInicioObra';
            case 'fechaFin':
                return 'fechaFinObra';
            case 'fechaPresentacionPrograma':
                return 'fechaPrograma';
            default:
                return name;
        }
    };

    const handleDateChange = (name: string, value: dayjs.Dayjs | null) => {
        const formattedValue = value ? value.format('YYYY-MM-DD') : null;

        handleChange({
            target: { name, value: formattedValue }
        } as any);

        if (setErrors) {
            const updatedFormData = { ...formData, [name]: formattedValue };

            const errorKey = getErrorKey(name);
            const error = validateField(errorKey, updatedFormData);

            const newErrors = { ...(errors || {}) };

            if (error) {
                newErrors[errorKey] = error;
            } else {
                delete newErrors[errorKey];
            }

            setErrors(newErrors);
        }
    };

    return (
        <>
            <SectionFormBox title={t('step1.section1.title')}>
                <Grid container spacing={1} sx={{ p: 2, mt: -6 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            required
                            name="calleRuta"
                            label={t('step1.section1.calle')}
                            value={formData.calleRuta || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors?.calleRuta}
                            helperText={errors?.calleRuta}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            required={!hasLatLng}
                            name="numeroKm"
                            label={t('step1.section1.numero')}
                            value={formData.numeroKm || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors?.numeroKm || (!!errors?.numeroKm && !hasLatLng)}
                            helperText={!hasLatLng ? errors?.numeroKm : ''}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            required={requiereLatLong}
                            name="latitud"
                            label={t('step1.section1.latitud')}
                            type="number"
                            inputProps={{ step: 'any' }}
                            value={formData.latitud || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors?.latitud || (requiereLatLong && !formData.latitud)}
                            helperText={errors?.latitud}
                            disabled={isView}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            required={requiereLatLong}
                            name="longitud"
                            label={t('step1.section1.longitud')}
                            type="number"
                            inputProps={{ step: 'any' }}
                            value={formData.longitud || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors?.longitud || (requiereLatLong && !formData.longitud)}
                            helperText={errors?.longitud}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            required
                            name="departamento"
                            label={t('step1.section1.depto')}
                            value={formData.departamento || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors?.departamento}
                            helperText={errors?.departamento}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <AutocompleteComponent
                            loading={loadingProvinces}
                            value={selectedProvince || null}
                            onChange={(selectedOption: any) => {
                                handleProvinceSelection(selectedOption);
                                handleChange({
                                    target: {
                                        name: 'idProvincia',
                                        value: selectedOption?.id ?? null
                                    }
                                } as any);
                                handleChange({
                                    target: {
                                        name: 'provinciaNombre',
                                        value: selectedOption?.nombre ?? ''
                                    }
                                } as any);
                            }}
                            name="idProvincia"
                            label={t('step1.section1.provincia')}
                            options={provincesList?.data?.provincias || []}
                            optionLabel={(option: any) => option.nombre}
                            required
                            errors={errors}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <AutocompleteComponent
                            loading={loadingCities}
                            value={selectedCity ?? null}
                            name="idLocalidad"
                            onChange={(selectedOption: any) => {
                                handleCitySelection(selectedOption);
                                handleChange({
                                    target: {
                                        name: 'idLocalidad',
                                        value: selectedOption?.id ?? null
                                    }
                                } as any);
                                handleChange({
                                    target: {
                                        name: 'localidadNombre',
                                        value: selectedOption?.nombre ?? ''
                                    }
                                } as any);
                            }}
                            options={selectedProvince ? citiesList?.data?.localidades || [] : []}
                            label={t('step1.section1.localidad')}
                            readOnly={!selectedProvince}
                            required
                            errors={errors}
                            onMouseDownCapture={(e: any) => {
                                if (!selectedProvince) {
                                    e.preventDefault();
                                    enqueueSnackbar('Seleccioná una provincia primero', {
                                        variant: 'info'
                                    });
                                }
                            }}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Input
                            value={selectedPostalCode || ''}
                            name="codigoPostal"
                            label={t<string>('step1.section1.codigoPostal')}
                            disabled={true}
                            onBlur={handleBlur}
                            required
                            errors={errors}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            fullWidth
                            required
                            name="superficieM2"
                            label={t('step1.section1.superficie')}
                            value={formData.superficieM2 || ''}
                            onChange={handleChange}
                            error={!!errors?.superficieM2}
                            helperText={errors?.superficieM2}
                            onBlur={handleBlur}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            fullWidth
                            required
                            name="cantidadPlantas"
                            label={t('step1.section1.numeroPlantas')}
                            value={formData.cantidadPlantas || ''}
                            onChange={handleChange}
                            error={!!errors?.cantidadPlantas}
                            helperText={errors?.cantidadPlantas}
                            onBlur={handleBlur}
                            disabled={isView}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            name="descripcionObra"
                            label={t('step1.section1.descripcion')}
                            value={formData.descripcionObra || ''}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            error={!!errors?.descripcionObra}
                            helperText={errors?.descripcionObra}
                            onBlur={handleBlur}
                            disabled={isView}
                        />
                    </Grid>
                </Grid>
            </SectionFormBox>
            <SectionFormBox title={t('step1.section2.title')}>
                <Grid container spacing={1} sx={{ p: 2, mt: -6 }}>
                    <Grid item xs={12}>
                        <AutocompleteComponent
                            loading={loadingResolutionTypes}
                            value={selectedResolutionType}
                            onChange={(option: TipoResolucionItem | null) => {
                                handleChange({
                                    target: { name: 'idTipoResolucion', value: option?.id ?? null }
                                } as any);

                                handleChange({
                                    target: {
                                        name: 'tipoResolucionName',
                                        value: option?.descripcion ?? ''
                                    }
                                } as any);

                                if (option?.requierePrograma === false) {
                                    handleChange({
                                        target: { name: 'fechaPresentacionPrograma', value: null }
                                    } as any);
                                    handleChange({
                                        target: { name: 'pdfPrograma', value: null }
                                    } as any);
                                }
                            }}
                            name="idTipoResolucion"
                            label={t('step1.section2.resolucionSeguridad')}
                            options={resolutionTypeOptions}
                            templateLabel={(option: any) => option?.name ?? ''}
                            optionLabel={(option: TipoResolucionItem) => option?.name ?? ''}
                            isOptionEqualToValue={(
                                opt: TipoResolucionItem,
                                val: TipoResolucionItem
                            ) => opt?.id === val?.id}
                            required
                            errors={errors}
                            disabled={isView}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                            <DatePicker
                                label={t('step1.section2.fechaPrograma')}
                                value={
                                    formData.fechaPresentacionPrograma
                                        ? dayjs(formData.fechaPresentacionPrograma)
                                        : null
                                }
                                disabled={
                                    isView || selectedResolutionType?.requierePrograma === false
                                }
                                format="DD/MM/YYYY"
                                onChange={(value) =>
                                    handleDateChange('fechaPresentacionPrograma', value)
                                }
                                slotProps={{
                                    textField: {
                                        required:
                                            selectedResolutionType?.descripcion !== 'A Presentar' &&
                                            selectedResolutionType?.descripcion !==
                                                'No Corresponde',
                                        fullWidth: true,
                                        error: !!errors?.fechaPresentacionPrograma,
                                        helperText: errors?.fechaPresentacionPrograma,
                                        inputProps: { placeholder: 'dd/mm/aaaa' }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        {formData.pdfProgramaUrl || idAvisoObra ? (
                            <Box sx={{ m: 0.4, p: 1.8, border: '1px solid #ccc', borderRadius: 1 }}>
                                <Typography
                                    variant="body1"
                                    color="primary"
                                    component="a"
                                    href={formData.pdfProgramaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    <CheckCircleIcon color="success" fontSize="small" />
                                    {formData.nombreArchivo ||
                                        (formData.pdfProgramaUrl
                                            ? formData.pdfProgramaUrl.split('/').pop()
                                            : '')}
                                </Typography>
                            </Box>
                        ) : !isView ? (
                            <FormControl fullWidth required sx={{ padding: '2px' }}>
                                <label
                                    htmlFor="pdfPrograma"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        border: '1px solid #ccc',
                                        borderRadius: 4,
                                        padding: '12px 16px',
                                        paddingRight: '5px',
                                        width: '100%',
                                        backgroundColor:
                                            selectedResolutionType?.requierePrograma === false
                                                ? '#f9f9f9'
                                                : 'transparent'
                                    }}
                                >
                                    <Typography
                                        fontSize={14}
                                        color={
                                            formData?.pdfPrograma
                                                ? 'text.primary'
                                                : 'text.secondary'
                                        }
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {formData?.pdfPrograma?.name || 'PDF *'}
                                    </Typography>
                                    <IconButton
                                        component="div"
                                        size="small"
                                        disabled={
                                            selectedResolutionType?.requierePrograma === false
                                        }
                                    >
                                        <UploadFileIcon />
                                    </IconButton>
                                </label>

                                <input
                                    id="pdfPrograma"
                                    type="file"
                                    name="pdfPrograma"
                                    accept="application/pdf"
                                    hidden
                                    disabled={selectedResolutionType?.requierePrograma === false}
                                    onChange={handleFileChange}
                                />

                                {selectedResolutionType?.requierePrograma && (
                                    <Typography variant="caption" color="text.secondary" mt={1}>
                                        El archivo PDF es obligatorio para esta resolución.
                                    </Typography>
                                )}
                            </FormControl>
                        ) : null}
                    </Grid>
                </Grid>
            </SectionFormBox>

            {isView && formData?.programaSeguridad?.length > 0 && (
                <SectionFormBox title={t('step1.section2.programaSeguridad')}>
                    <Grid container spacing={1} sx={{ p: 2, mt: -6 }}>
                        {formData.programaSeguridad.map((doc: any) => {
                            const fileName = decodeURIComponent(
                                doc.urlArchivo?.split('/').pop() || ''
                            );
                            return (
                                <Grid item xs={6} key={doc.idPrograma}>
                                    <Box
                                        sx={{
                                            m: 0.4,
                                            p: 1.2,
                                            border: '1px solid #ccc',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="primary"
                                            component="a"
                                            href={doc.urlArchivo}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                                '&:hover': { textDecoration: 'underline' },
                                                marginRight: 2
                                            }}
                                        >
                                            <CheckCircleIcon color="success" fontSize="small" />
                                            {fileName}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(doc.fechaPresentacion).format('DD/MM/YYYY')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </SectionFormBox>
            )}
            <SectionFormBox title={t('step1.section3.title')}>
                {formData.fechaInicio &&
                    formData.fechaFin &&
                    dayjs(formData.fechaFin).diff(dayjs(formData.fechaInicio), 'year', true) >
                        1 && (
                        <Box mb={5} px={2}>
                            <Alert severity="info" variant="outlined">
                                Al tener una fecha de finalización de obra superior a un año, al
                                final del mismo, deberá crear un Establecimiento con la respectiva
                                presentación de RGRL.
                            </Alert>
                        </Box>
                    )}

                <Grid container spacing={1} sx={{ p: 2, mt: -6 }}>
                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                            <DatePicker
                                label={t('step1.section3.fechaInicioObra')}
                                value={formData.fechaInicio ? dayjs(formData.fechaInicio) : null}
                                maxDate={formData.fechaFin ? dayjs(formData.fechaFin) : undefined}
                                disabled={isView}
                                format="DD/MM/YYYY"
                                onChange={(value) => handleDateChange('fechaInicio', value)}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        error: !!errors?.fechaInicioObra,
                                        helperText: errors?.fechaInicioObra,
                                        inputProps: { placeholder: 'dd/mm/aaaa' }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                            <DatePicker
                                label={t('step1.section3.fechaFinObra')}
                                value={formData.fechaFin ? dayjs(formData.fechaFin) : null}
                                disabled={isView}
                                minDate={
                                    formData.fechaInicio ? dayjs(formData.fechaInicio) : undefined
                                }
                                format="DD/MM/YYYY"
                                onChange={(value) => handleDateChange('fechaFin', value)}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        error: !!errors?.fechaFinObra,
                                        helperText: errors?.fechaFinObra,
                                        inputProps: { placeholder: 'dd/mm/aaaa' }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
            </SectionFormBox>
        </>
    );
};

export default Step1;
